import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styles from "./DealerShipDetails.module.scss";
import { Button, Grid, Box } from "@mui/material";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import MultiToggleWidget from "../../../shared/toggles/multiToggleWidget/MultiToggleWidget";
import EditIcon from "../../../shared/Edit/EditIcon";
import ClearIcon from "@mui/icons-material/Clear";
import { withTranslation } from "react-i18next";
import HasPermissionTo from "../../../common/HasPermissionTo";
import DealerDetailsClient from "../../../clients/DealerDetailsClient";
import UpperCaseText from "../../../utils/UpperCaseText";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";

const renderEmptyGridMd2 = () => <Grid item md={2} />;

const getEmployeeKey = (employee) => `employee_${employee.id}`;

const DealerEmployeeTable = (props) => {
  const {
    dealers,
    user,
    disableLiveUpdateEmployees,
    enableLiveUpdateEmployees,
    isMainViewingSDLR,
    renderAgain,
    t,
  } = props;

  const currentDealer = dealers;
  const rolePermissions = user.permissions.rolePermissions;

  const [employees, setEmployees] = useState([]);
  const [addNewRowActive, setAddNewRowActive] = useState(false);
  const [employeesToEdit, setEmployeesToEdit] = useState(new Map());
  const [employeeLimitReached, setEmployeeLimitReached] = useState(false);

  const refreshNewEmployee = () => ({
    id: "",
    dealerId: currentDealer.completeDealerId,
    fullName: "",
    emailAddress: "",
    receiveEmailsForNotifications: true,
    receiveEmailsForPendingActions: true,
  });

  const [newEmployee, setNewEmployee] = useState(refreshNewEmployee);

  const getDealerEmployees = () => {
    DealerDetailsClient.getDealerEmployees(
      user,
      UpperCaseText(user.userType) === "FIELD_VIEW" &&
        UpperCaseText(user.market) === "CAN"
        ? `CAN${currentDealer.paCode}`
        : currentDealer.completeDealerId
    )
      .then((employeeList) => {
        setEmployees(employeeList);
        setEmployeeLimitReached(employeeList.length > 9);
        disableLiveUpdateEmployees();
      })
      .catch(() => {});
  };

  useEffect(() => {
    getDealerEmployees();
  }, []);

  useEffect(() => {
    if (renderAgain) {
      getDealerEmployees();
    }
  }, [renderAgain]);

  const refreshEmployees = () => {
    enableLiveUpdateEmployees();
  };

  const resetRow = (employeeId) => {
    const empId = Number.parseInt(employeeId, 10);
    setEmployeesToEdit((prev) => {
      if (!prev.has(empId)) return prev;
      const copy = new Map(prev);
      copy.delete(empId);
      return copy;
    });
  };

  const onSelectEmailNotification = (employee, event) => {
    const selectedStatus = event.target.checked;

    DealerDetailsClient.updateDealerEmployeeEmailNotifications(
      user,
      employee,
      selectedStatus
    ).then(() => {
      const copy = [...employees];
      const existingEmployee = employees.find((emp) => emp.id === employee.id);
      const index = employees.indexOf(existingEmployee);
      const employeeToChange = copy[index];
      employeeToChange.receiveEmailsForNotifications = selectedStatus;
      copy.splice(index, 1, employeeToChange);
      setEmployees(copy);
      resetRow(employee.id);
    });
  };

  const onSelectPendingActionNotification = (employee, event) => {
    const selectedStatus = event.target.checked;

    DealerDetailsClient.updateDealerEmployeePendingActionsEmails(
      user,
      employee,
      selectedStatus
    ).then(() => {
      const copy = [...employees];
      const existingEmployee = employees.find((emp) => emp.id === employee.id);
      const index = employees.indexOf(existingEmployee);
      const employeeToChange = copy[index];
      employeeToChange.receiveEmailsForPendingActions = selectedStatus;
      copy.splice(index, 1, employeeToChange);
      setEmployees(copy);
      resetRow(employee.id);
    });
  };

  const editExistingEmployee = (event) => {
    const editEmployee = employees.find(
      (employee) =>
        employee.id === Number.parseInt(event.currentTarget.value, 10)
    );
    const copy = new Map(employeesToEdit);
    copy.set(editEmployee.id, editEmployee);
    setEmployeesToEdit(copy);
  };

  const deleteExistingEmployee = (event) => {
    const employeeId = Number.parseInt(event.currentTarget.value, 10);
    DealerDetailsClient.deleteDealerEmployee(user, employeeId).then(() => {
      resetRow(employeeId);
      const copy = [...employees];
      const existingEmployee = employees.find((emp) => emp.id === employeeId);
      const index = employees.indexOf(existingEmployee);
      copy.splice(index, 1);
      setEmployees(copy);
      setEmployeeLimitReached(copy.length > 9);
    });
  };

  const editEmployeeNameOnChange = (employeeId, event) => {
    const modifiedName = event.target.value;
    setEmployeesToEdit((prevMap) => {
      const copy = new Map(prevMap);
      const newEmployee = {
        ...copy.get(employeeId),
        fullName: modifiedName,
      };
      copy.set(employeeId, newEmployee);
      return copy;
    });
  };

  const editEmployeeEmailOnChange = (employeeId, event) => {
    const modifiedEmail = event.target.value;
    setEmployeesToEdit((prevMap) => {
      const copy = new Map(prevMap);
      const newEmployee = {
        ...copy.get(employeeId),
        emailAddress: modifiedEmail,
      };
      copy.set(employeeId, newEmployee);
      return copy;
    });
  };

  const updateEmployee = (employee) => {
    DealerDetailsClient.updateDealerEmployee(user, employee).then(
      (updateEmployee) => {
        const copy = [...employees];
        const existingEmployee = employees.find(
          (emp) => emp.id === updateEmployee.id
        );
        const index = employees.indexOf(existingEmployee);
        copy.splice(index, 1, updateEmployee);
        setEmployees(copy);
        resetRow(updateEmployee.id);
        refreshEmployees();
      }
    );
  };

  const cancelEdit = (event) => {
    resetRow(event.currentTarget.value);
  };

  const addNewEmployee = () => {
    DealerDetailsClient.addEmployee(
      user,
      newEmployee,
      currentDealer.completeDealerId
    ).then((newEmployee) => {
      const updatedEmployees = employees.concat(newEmployee);
      setEmployees(updatedEmployees);
      setEmployeeLimitReached(updatedEmployees.length > 9);
      setAddNewRowActive(false);
      setNewEmployee(refreshNewEmployee());
      refreshEmployees();
    });
  };

  const cancelNewEmployee = () => {
    setAddNewRowActive(false);
    setNewEmployee(refreshNewEmployee());
  };

  const showNewEmployeeRow = () => {
    setAddNewRowActive(true);
  };

  const newEmployeeEmailOnChange = (event) => {
    setNewEmployee({
      ...newEmployee,
      emailAddress: event.target.value,
    });
  };

  const newEmployeeNameOnChange = (event) => {
    setNewEmployee({
      ...newEmployee,
      fullName: event.target.value,
    });
  };

  const isViewingMainSDLR = isMainViewingSDLR(dealers);
  const canShowEditableToggles =
    isViewingMainSDLR === false && !user.isDealerUsedCarsSalesManager;

  return (
    <>
      <div className={styles.employeeEmailHeader}>
        <div className={styles.sectionHeaderEmployees}>
          {t("DealerShipDetails.employeeHeader")}
        </div>
        {employees.length > 0 && (
          <>
            <Grid container className={styles.subHeader}>
              <Grid item md={3} className={styles.employeeInfoHeader}>
                {t("DealerShipDetails.employees")}
              </Grid>
              <Grid item md={3} className={styles.employeeInfoHeader}>
                {t("DealerShipDetails.employeesEmail")}
              </Grid>
              <Grid item md={2} className={styles.notifications}>
                <div>{t("DealerShipDetails.employeesNotifications")}</div>
              </Grid>

              <Grid item md={2} className={styles.notifications}>
                <HasPermissionTo
                  perform={["receivePendingActionColumn"]}
                  permissions={rolePermissions}
                  render={() => (
                    <div>{t("DealerShipDetails.employeesPendingActions")}</div>
                  )}
                />
              </Grid>
              <Grid item md={2} className={styles.employeeInfoHeader}>
                &nbsp;
              </Grid>
            </Grid>

            {employees.map((employee) => (
              <div key={getEmployeeKey(employee)}>
                {employeesToEdit.has(employee.id) ? (
                  <ValidatorForm
                    onSubmit={() =>
                      updateEmployee(employeesToEdit.get(employee.id))
                    }
                  >
                    <Grid
                      container
                      className={`${styles.tableBody} ${styles.employeeInfo}`}
                    >
                      <Grid item md={3}>
                        <Box pr={2}>
                          <TextValidator
                            name={`fullName_${employee.id}`}
                            value={employeesToEdit.get(employee.id).fullName}
                            validators={["required"]}
                            onChange={(e) =>
                              editEmployeeNameOnChange(employee.id, e)
                            }
                            errorMessages={[t("Error.requiredField")]}
                          />
                        </Box>
                      </Grid>
                      <Grid item md={3}>
                        <Box pr={2}>
                          <TextValidator
                            name={`email_${employee.id}`}
                            value={
                              employeesToEdit.get(employee.id).emailAddress
                            }
                            validators={["required", "isEmail"]}
                            onChange={(e) =>
                              editEmployeeEmailOnChange(employee.id, e)
                            }
                            errorMessages={[
                              t("Error.requiredField"),
                              t("Error.invalidInput"),
                            ]}
                          />
                        </Box>
                      </Grid>
                      <Grid item className={styles.employeeSwitches} md={2}>
                        {employee.receiveEmailsForNotifications
                          ? t("DealerShipDetails.yes")
                          : t("DealerShipDetails.no")}
                      </Grid>
                      <HasPermissionTo
                        perform={["receivePendingActionColumn"]}
                        permissions={rolePermissions}
                        render={() => (
                          <Grid item className={styles.employeeSwitches} md={2}>
                            {employee.receiveEmailsForPendingActions
                              ? t("DealerShipDetails.yes")
                              : t("DealerShipDetails.no")}
                          </Grid>
                        )}
                        noRender={renderEmptyGridMd2}
                      />
                      <Grid item md={2} className={styles.editDiv}>
                        <EMPButton
                          testId={`updateEmployee_${employee.id}`}
                          submitButton
                          disabled={UpperCaseText(user.userType) === "FIELD_VIEW"}
                        >
                          {t("BusinessSettings.save")}
                        </EMPButton>
                        <EMPButton
                          testId={`cancelEdit_${employee.id}`}
                          value={employee.id}
                          onClick={cancelEdit}
                        >
                          {t("BusinessSettings.cancelButton")}
                        </EMPButton>
                      </Grid>
                    </Grid>
                  </ValidatorForm>
                ) : (
                  <Grid container className={styles.employeeInfo}>
                    <Grid
                      item
                      md={3}
                      id={`name_${employee.id}`}
                      className={styles.employeeName}
                    >
                      {employee.fullName}
                    </Grid>
                    <Grid
                      item
                      md={3}
                      id={`email_${employee.id}`}
                      className={styles.employeeEmail}
                    >
                      {employee.emailAddress}
                    </Grid>

                    <>
                      {canShowEditableToggles ? (
                        <>
                          <Grid item md={2} className={styles.switches}>
                            <MultiToggleWidget
                              settings={{
                                acceptedText: t("DealerShipDetails.yes"),
                                rejectedText: t("DealerShipDetails.no"),
                                suppClass: `${styles.toggleWrapper}`,
                                toggleLabel: `${styles.activationToggle}`,
                              }}
                              fromDealerTable={true}
                              isMainViewingSDLR=""
                              toggleName={`emailNot_${employee.id}`}
                              currentStatus={
                                employee.receiveEmailsForNotifications
                                  ? "yes"
                                  : "no"
                              }
                              onSelect={(e) =>
                                onSelectEmailNotification(employee, e)
                              }
                            />
                          </Grid>
                          <HasPermissionTo
                            perform={["receivePendingActionColumn"]}
                            permissions={rolePermissions}
                            render={() => (
                              <Grid item md={2} className={styles.switches}>
                                <MultiToggleWidget
                                  settings={{
                                    acceptedText: t("DealerShipDetails.yes"),
                                    rejectedText: t("DealerShipDetails.no"),
                                    suppClass: `${styles.toggleWrapper}`,
                                    toggleLabel: `${styles.activationToggle}`,
                                  }}
                                  fromDealerTable={true}
                                  toggleName={`pendingAcNot_${employee.id}`}
                                  currentStatus={
                                    employee.receiveEmailsForPendingActions
                                      ? "yes"
                                      : "no"
                                  }
                                  onSelect={(e) =>
                                    onSelectPendingActionNotification(
                                      employee,
                                      e
                                    )
                                  }
                                />
                              </Grid>
                            )}
                            noRender={renderEmptyGridMd2}
                          />
                        </>
                      ) : (
                        <>
                          <Grid
                            item
                            md={2}
                            id={`toggle1_${employee.id}`}
                            className={`${styles.employeeSwitches} switch-3 switch-candy`}
                          >
                            {employee.receiveEmailsForNotifications
                              ? t("DealerShipDetails.yes")
                              : t("DealerShipDetails.no")}
                          </Grid>
                          <Grid
                            item
                            md={2}
                            id={`toggle2_${employee.id}`}
                            className={`${styles.employeeSwitches} switch-3 switch-candy`}
                          >
                            <HasPermissionTo
                              perform={["receivePendingActionColumn"]}
                              permissions={rolePermissions}
                              render={() => (
                                <div>
                                  {employee.receiveEmailsForPendingActions
                                    ? t("DealerShipDetails.yes")
                                    : t("DealerShipDetails.no")}
                                </div>
                              )}
                            />
                          </Grid>
                        </>
                      )}

                      <HasPermissionTo
                        perform={["dealershipDetails:write"]}
                        permissions={rolePermissions}
                        condition={isViewingMainSDLR === false}
                        render={() => (
                          <Grid item md={2} className={styles.editDiv}>
                            <Button
                              id={`editEmployee_${employee.id}`}
                              data-testid={`editEmployee_${employee.id}`}
                              value={employee.id}
                              onClick={editExistingEmployee}
                            >
                              <EditIcon />
                            </Button>
                            <Button
                              id={`deleteEmployee_${employee.id}`}
                              data-testid={`deleteEmployee_${employee.id}`}
                              value={employee.id}
                              onClick={deleteExistingEmployee}
                            >
                              <ClearIcon />
                            </Button>
                          </Grid>
                        )}
                      />
                    </>
                  </Grid>
                )}
              </div>
            ))}
          </>
        )}
        {addNewRowActive && (
          <ValidatorForm onSubmit={addNewEmployee}>
            <Grid className={styles.newEmployeeContainer} container>
              <Grid item md={3}>
                <TextValidator
                  style={{ width: "70%" }}
                  name={`inputName_${currentDealer.regionalId}`}
                  value={newEmployee.fullName}
                  placeholder={t("DealerShipDetails.fullName")}
                  onChange={newEmployeeNameOnChange}
                  validators={["required"]}
                  errorMessages={[t("Error.requiredField")]}
                />
              </Grid>
              <Grid item md={3}>
                <TextValidator
                  style={{ width: "70%" }}
                  name={`inputEmail_${currentDealer.regionalId}`}
                  value={newEmployee.emailAddress}
                  placeholder={t("DealerShipDetails.emailAddress")}
                  onChange={newEmployeeEmailOnChange}
                  validators={["required", "isEmail"]}
                  errorMessages={[
                    t("Error.requiredField"),
                    t("Error.invalidInput"),
                  ]}
                />
              </Grid>
              <Grid item md={4} />
              <Grid className={styles.actionDiv} item md={2}>
                <EMPButton
                  testId={`submit_${currentDealer.regionalId}`}
                  submitButton
                  disabled={UpperCaseText(user.userType) === "FIELD_VIEW"}
                >
                  {t("BusinessSettings.save")}
                </EMPButton>
                <EMPButton
                  testId={`cancelAdd_${currentDealer.regionalId}`}
                  onClick={cancelNewEmployee}
                >
                  {t("BusinessSettings.cancelButton")}
                </EMPButton>
              </Grid>
            </Grid>
          </ValidatorForm>
        )}
      </div>
      <HasPermissionTo
        perform={["dealershipDetails:write"]}
        permissions={rolePermissions}
        condition={isViewingMainSDLR === false}
        render={() => (
          <div className="pagePaddingLeft pagePaddingRight">
            <Box mt={2}>
              <EMPButton
                testId={`addEmployee_${currentDealer.regionalId}`}
                disabled={addNewRowActive || employeeLimitReached}
                onClick={showNewEmployeeRow}
                buttonElement="button"
                variant="contained"
              >
                {t("DealerShipDetails.addButton")}
              </EMPButton>

              {employeeLimitReached && (
                <div className={styles.maximumEmployee}>
                  {t("DealerShipDetails.maxEmployee")}
                </div>
              )}
            </Box>
          </div>
        )}
      />
    </>
  );
};

const dealerShape = PropTypes.shape({
  completeDealerId: PropTypes.string,
  paCode: PropTypes.string,
  regionalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

const userShape = PropTypes.shape({
  isDealerUsedCarsSalesManager: PropTypes.bool,
  market: PropTypes.string,
  permissions: PropTypes.shape({
    rolePermissions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  }),
  userType: PropTypes.string,
});

DealerEmployeeTable.propTypes = {
  dealers: dealerShape.isRequired,
  disableLiveUpdateEmployees: PropTypes.func.isRequired,
  enableLiveUpdateEmployees: PropTypes.func.isRequired,
  isMainViewingSDLR: PropTypes.func.isRequired,
  renderAgain: PropTypes.bool,
  t: PropTypes.func.isRequired,
  user: userShape.isRequired,
};

DealerEmployeeTable.defaultProps = {
  renderAgain: false,
};

export default withTranslation("emp")(DealerEmployeeTable);
